/**
 * Bootstrap Service
 * Handles the bootstrap token redemption flow.
 *
 * FLOW (v2):
 *   1. Host app calls POST /api/v1/bootstrap (Initiate) with { flow, locale, input, metadata }
 *      and its Bearer token in Authorization header.
 *   2. Initiate returns a LaunchUrl containing ?token=<sha256>.
 *   3. Host opens the iframe/webview using that LaunchUrl directly.
 *   4. UI extracts ?token= from URL and sends it via x-bootstrap-token header to
 *      POST /api/v1/redeem (no request body).
 *   5. Backend returns { flow, locale, accessToken, metadata: { companyId, erpProductCode,
 *      version, input } }.
 *   6. flow, locale, datasetId (= companyId), accessToken, and metadata.input are all
 *      set from the response.
 */

import axios, { type AxiosError } from "axios";

import type { EInvoiceOnboardingInput } from "@/types/flows/onboarding.types";
import type { ReceivedEInvoicesInput } from "@/types/flows/received-einvoices.types";
import type {
  BootstrapParams,
  EInvoiceOnboardingSession,
  FlowSession,
  ReceivedEInvoicesSession,
  RedeemApiResponse
} from "@/types/session.types";
import { environment } from "@constants/environment";
import { loggerClient } from "@utils/logger";

/**
 * Extract the one-time launch token from the current URL search params.
 * The backend Initiate API returns a LaunchUrl with ?token= already set.
 * flow, locale, and metadata are NOT URL params — they come from the
 * backend's cached session entry, returned by the Redeem endpoint.
 */
export function extractBootstrapParams(): BootstrapParams | null {
  const params = new URLSearchParams(globalThis.location.search);
  const token = params.get("token");

  if (!token) {
    return null;
  }

  return { token };
}

/**
 * Redeem a bootstrap token with the backend API.
 *
 * POST /api/v1/redeem
 * Header: x-bootstrap-token: <token>
 * Header: Idempotency-Key: <uuid>
 * Body: none
 *
 * Returns a FlowSession built from the decoded session entry.
 * flow, locale, and all metadata — including the full input envelope —
 * come entirely from the backend response.
 */

function handleAxiosBootstrapError(err: AxiosError): never {
  const status = err.response?.status;

  if (status === 404 || status === 410) {
    throw new Error(
      "Bootstrap token is invalid or expired. Please refresh and try again."
    );
  }

  if (status === 400) {
    const errorData = err.response?.data as RedeemApiResponse | undefined;
    const msg = errorData?.messages?.errors?.[0];
    throw new Error(msg ?? "Invalid bootstrap token format");
  }

  if (status && status >= 500) {
    throw new Error(
      "Backend service is temporarily unavailable. Please try again."
    );
  }

  if (err.code === "ECONNABORTED") {
    throw new Error("Request timeout. Backend service is not responding.");
  }

  if (!err.response) {
    throw new Error(
      "Network error. Please check your internet connection and try again."
    );
  }

  throw new Error(`Failed to redeem token (${status ?? "unknown error"})`);
}

export async function redeemBootstrapToken(
  params: BootstrapParams
): Promise<FlowSession> {
  try {
    const { data: response } = await axios.post<RedeemApiResponse>(
      `${environment.REACT_APP_E_INVOICING_API_ENDPOINT}v1/redeem`,
      null,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "x-bootstrap-token": params.token,
          "Idempotency-Key": crypto.randomUUID()
        },
        timeout: 10000
      }
    );

    if (!response.success || !response.data) {
      const errorMsg =
        response.messages?.errors?.[0] || "Failed to redeem bootstrap token";
      loggerClient.error("[Bootstrap] Redeem API returned error", { errorMsg });
      throw new Error(errorMsg);
    }

    const { flow, locale, accessToken, datasetId, input, metadata } =
      response.data;
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour

    const baseSession = {
      sessionId: `session-${crypto.randomUUID()}`,
      status: "active" as const,
      datasetId: datasetId ?? "",
      groupKey: datasetId ?? "",
      authToken: accessToken,
      locale: locale ?? "en-GB",
      expiresAt: expiresAt.toISOString()
    };

    if (flow === "received_einvoices") {
      const session: ReceivedEInvoicesSession = {
        ...baseSession,
        flow: "received_einvoices",
        metadata: {
          companyId: datasetId ?? undefined,
          accessToken,
          input: (input as ReceivedEInvoicesInput) ?? undefined
        }
      };
      return session;
    }

    // Default: einvoice_onboarding
    const onboardingInput = input as EInvoiceOnboardingInput | null;
    const session: EInvoiceOnboardingSession = {
      ...baseSession,
      flow: "einvoice_onboarding",
      metadata: {
        companyId: datasetId ?? undefined,
        accessToken,
        erpProductCode: metadata?.erpProductCode ?? undefined,
        version: metadata?.version ?? undefined,
        integrationMode: onboardingInput?.integrationMode ?? undefined,
        productDisplayName: onboardingInput?.productDisplayName ?? undefined,
        input: onboardingInput ?? undefined
      }
    };
    return session;
  } catch (err) {
    loggerClient.error(
      "[Bootstrap] Token redemption failed",
      err instanceof Error ? err : new Error(String(err))
    );

    // Handle axios errors with better messages
    if (axios.isAxiosError(err)) {
      handleAxiosBootstrapError(err);
    }

    // Non-axios errors
    if (err instanceof Error) {
      throw err;
    }

    throw new Error("An unexpected error occurred during bootstrap");
  }
}

/**
 * Check whether a session has expired based on its expiresAt timestamp.
 */
export function isSessionExpired(session: FlowSession): boolean {
  return new Date(session.expiresAt).getTime() < Date.now();
}
